import React from 'react';

import {
  AdoConnectionStatus,
  adoIntegrationService,
} from '../../services/adoIntegrationService';

import { REDIRECT_PATH, SESSION_RETURN_KEY } from './AdoOAuthCallbackPage';

interface AdoConnectionPanelProps {
  status: AdoConnectionStatus | null;
  loading: boolean;
  onChange: () => void;
  returnTo?: string;
}

/**
 * Reusable card showing the user's ADO SSO state with a Connect /
 * Disconnect button. Stores the post-callback return URL in
 * sessionStorage so the OAuth callback page can send the user back to
 * the right spot. An expired token is treated as disconnected — we
 * surface the Connect button rather than pretending the session is
 * live.
 */
const AdoConnectionPanel: React.FC<AdoConnectionPanelProps> = ({
  status,
  loading,
  onChange,
  returnTo,
}) => {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const expiresAt = parseUtcDate(status?.expires_at);
  const tokenExpired = expiresAt
    ? Number.isFinite(expiresAt.getTime()) && expiresAt.getTime() <= Date.now()
    : false;
  const effectiveConnected = Boolean(status?.connected) && !tokenExpired;

  const handleConnect = async () => {
    setBusy(true);
    setError(null);
    try {
      const redirectUri = `${globalThis.location.origin}${REDIRECT_PATH}`;
      globalThis.sessionStorage.setItem(
        SESSION_RETURN_KEY,
        returnTo ?? globalThis.location.pathname,
      );
      const res = await adoIntegrationService.startConnection(redirectUri);
      globalThis.location.href = res.authorization_url;
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    setBusy(true);
    setError(null);
    try {
      await adoIntegrationService.disconnect();
      onChange();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-lg border border-border-medium bg-background-surface shadow-sm">
        <div className="px-5 py-4 flex items-center gap-2 text-text-onSurface-subtle">
          <span className="loading loading-spinner loading-sm" />
          <span>Loading Azure DevOps connection…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border-medium bg-background-surface shadow-sm">
      <div className="px-5 py-4 flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-text-onSurface-strong">
              Azure DevOps connection
            </h3>
            <p className="text-xs text-text-onSurface-subtle">
              Used to list your organizations and projects when sending an
              architecture to Codie.
            </p>
          </div>
          {effectiveConnected ? (
            <ConnectionPill tone="success" label="Connected" />
          ) : tokenExpired ? (
            <ConnectionPill tone="warning" label="Session expired" />
          ) : (
            <ConnectionPill tone="neutral" label="Not connected" />
          )}
        </div>

        {error && (
          <div className="rounded border border-system-strong-negative/30 bg-system-subtle-negative px-3 py-2 text-sm text-system-strong-negative">
            {error}
          </div>
        )}

        {effectiveConnected ? (
          <ConnectedBody
            email={status?.ado_user_email}
            expiresAt={expiresAt}
            busy={busy}
            onDisconnect={handleDisconnect}
          />
        ) : (
          <DisconnectedBody
            tokenExpired={tokenExpired}
            busy={busy}
            onConnect={handleConnect}
          />
        )}
      </div>
    </div>
  );
};

interface ConnectedBodyProps {
  email: string | null | undefined;
  expiresAt: Date | null;
  busy: boolean;
  onDisconnect: () => void;
}

/** Signed-in state: shows user email, session expiry and Disconnect button. */
const ConnectedBody: React.FC<ConnectedBodyProps> = ({
  email,
  expiresAt,
  busy,
  onDisconnect,
}) => (
  <div className="flex flex-wrap items-end justify-between gap-3">
    <div className="min-w-0">
      <p className="text-sm text-text-onSurface-strong">
        Signed in as{' '}
        <span className="font-mono font-medium">{email}</span>
      </p>
      {expiresAt && (
        <p className="text-xs text-text-onSurface-subtle">
          Session expires {expiresAt.toLocaleString()}
        </p>
      )}
    </div>
    <button
      type="button"
      onClick={onDisconnect}
      disabled={busy}
      className="px-4 py-2 rounded font-medium text-sm border border-system-strong-negative text-system-strong-negative bg-background-surface hover:bg-system-subtle-negative transition disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {busy ? 'Disconnecting…' : 'Disconnect'}
    </button>
  </div>
);

interface DisconnectedBodyProps {
  tokenExpired: boolean;
  busy: boolean;
  onConnect: () => void;
}

/** Not-yet-connected / expired state: copy + Connect button. */
const DisconnectedBody: React.FC<DisconnectedBodyProps> = ({
  tokenExpired,
  busy,
  onConnect,
}) => {
  const message = tokenExpired
    ? 'Your Azure DevOps session has expired. Sign in again to continue sending architectures to Codie.'
    : 'Sign in with your work account to let Archie discover your ADO organizations and projects.';

  const buttonLabel = busy
    ? 'Redirecting…'
    : tokenExpired
      ? 'Sign in again'
      : 'Connect to Azure DevOps';

  return (
    <div className="flex flex-wrap items-end justify-between gap-3">
      <p className="text-sm text-text-onSurface-strong max-w-prose">
        {message}
      </p>
      <button
        type="button"
        onClick={onConnect}
        disabled={busy}
        className="px-5 py-2.5 rounded font-semibold text-sm main-button disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {buttonLabel}
      </button>
    </div>
  );
};

interface ConnectionPillProps {
  tone: 'success' | 'warning' | 'neutral';
  label: string;
}

const ConnectionPill: React.FC<ConnectionPillProps> = ({ tone, label }) => {
  const classes =
    tone === 'success'
      ? 'bg-system-subtle-positive text-system-strong-positive border-system-strong-positive/30'
      : tone === 'warning'
        ? 'bg-system-subtle-warning text-text-onSurface-strong border-system-strong-warning/40'
        : 'bg-system-subtle-neutral text-text-onSurface-subtle border-border-medium';
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${classes}`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${
          tone === 'success'
            ? 'bg-system-strong-positive'
            : tone === 'warning'
              ? 'bg-system-strong-warning'
              : 'bg-border-strong'
        }`}
      />
      {label}
    </span>
  );
};

export default AdoConnectionPanel;

/**
 * Parse an ISO timestamp coming from the backend. Archie stores ADO
 * `expires_at` as a naive UTC datetime (no offset), which `new Date(...)`
 * would otherwise misinterpret as the browser's local time. Append `Z`
 * when no explicit timezone marker is present so the value is treated
 * as UTC.
 */
function parseUtcDate(value: string | null | undefined): Date | null {
  if (!value) {return null;}
  const hasTz = /(Z|[+-]\d{2}:?\d{2})$/i.test(value);
  return new Date(hasTz ? value : `${value}Z`);
}
